import { CommonIP, CommonIPLite, CommonIPState, CommonIPStateTrifoldFiltersDialogData, IP, WorkflowDO, WsFile } from '@model/ftf-api.model';
import { createAction, props } from '@ngrx/store';

/**
 * This action is for adding an item that has not yet been fetched from the API.
 * This action will trigger an API call to fetch the item. If the item is successfully
 * fetched, the `addItemSuccess` action will be dispatched. If the item cannot be
 * fetched, the `addItemFailure` action will be dispatched.
 */
export const addItemLite = createAction(
  '[Items] Add Item Lite',
  props<{ item: CommonIPLite; skipReference: boolean; skipWorkflow: boolean; trifoldFilters: CommonIPStateTrifoldFiltersDialogData }>()
);

/**
 * This action is dispatched when an item is successfully added to the store.
 */
export const addItemSuccess = createAction('[Items] Add Item Success', props<{ item: CommonIP }>());

/**
 * This action is dispatched when there is a failure in adding an item to the store.
 */
export const addItemFailure = createAction('[Items] Add Item Failure', props<{ error: unknown }>());

/**
 * This action is for removing an item from the store.
 */
export const removeItem = createAction('[Items] Remove Item', props<{ item: CommonIPLite }>());

/**
 * This action is for updating an existing item in the store.
 */
export const updateItem = createAction('[Items] Update Item', props<{ item: CommonIPLite & Partial<CommonIP>; skipState: boolean }>());

/**
 * This action is for updating a trifold document associated with an item.
 */
export const updateItemTrifoldDocument = createAction(
  '[Items] Update Item Trifold Document',
  props<{ item: CommonIPLite; trifold?: IP.TrifoldEnum; id: number; doc: Partial<WsFile> }>()
);

/**
 * This action is for updating the workflow of an item.
 */
export const updateItemWorkflow = createAction(
  '[Items] Update Item Workflow',
  props<{ item: CommonIPLite; id: number; workflow: Partial<WorkflowDO> }>()
);

/**
 * This action is for refreshing items in the store.
 */
export const refreshItems = createAction(
  '[Items] Refresh Items',
  props<{ refreshItems: CommonIPLite[]; skipReference: boolean; skipWorkflow: boolean }>()
);

/**
 * This action is dispatched when items are successfully loaded into the store.
 */
export const loadItemsSuccess = createAction('[Items] Load Items Success', props<{ items: CommonIP[] }>());

/**
 *
 */
export const loadItemsFailure = createAction('[Items] Load Items Failure', props<{ error: unknown }>());

/**
 * This action is for updating the state of an item in the store.
 */
export const updateItemState = createAction(
  '[Items] Update Item State',
  props<{ item: CommonIPLite; itemState: Partial<CommonIPState> }>()
);

/**
 * This action is for resetting all items in the store.
 */
export const resetItems = createAction('[Items] Reset Items');

/**
 * This action is for updating the document status of an item.
 */
export const updateDocStatus = createAction(
  '[Items] Update Document Status',
  props<{ item: CommonIPLite; id: number; action: 'lock' | 'unlock' | 'undocheckout' | 'checkout' }>()
);

/**
 * This action is for downloading an item's document.
 */
export const downloadItemDoc = createAction('[Items] Download Item Document', props<{ fileName: string; data: Blob }>());

/**
 * This action is for deleting an item's document.
 */
export const deleteItemDoc = createAction('[Items] Delete Item Doc', props<{ item: CommonIPLite; id: number }>());
